import { useRef, useSyncExternalStore } from "react";
import { useLanggraphStreamContext } from "./SmartStreamProvider";
import type { SmartStreamStore } from "./SmartStreamStore";
import type { StreamSnapshot } from "./types";
import { shallowEqual } from "./shallowEqual";

/**
 * Subscribe to a slice of stream state via a selector.
 * Uses shallow equality to prevent re-renders when the selected value hasn't changed.
 *
 * Generic parameter `StateType` recovers type safety for `snapshot.values`:
 *
 * @example
 * ```tsx
 * // Typed — full autocomplete on values
 * const topic = useStreamSelector<BrainstormGraphState>((s) => s.values.currentTopic);
 *
 * // Untyped — values is Record<string, unknown>
 * const isLoading = useStreamSelector((s) => s.isLoading);
 * ```
 */
export function useStreamSelector<
  Selected,
  StateType extends Record<string, unknown> = Record<string, unknown>,
>(
  selector: (snapshot: StreamSnapshot<StateType>) => Selected
): Selected {
  const store = useLanggraphStreamContext<StateType>();
  return useStreamSelectorWithStore(store, selector);
}

/**
 * Same as useStreamSelector but takes a store directly instead of from context.
 * Useful when you have the store reference (e.g., from useLanggraphStream return value).
 */
export function useStreamSelectorWithStore<
  Selected,
  StateType extends Record<string, unknown> = Record<string, unknown>,
>(
  store: SmartStreamStore<StateType, any>,
  selector: (snapshot: StreamSnapshot<StateType>) => Selected
): Selected {
  const prevRef = useRef<Selected | undefined>(undefined);

  return useSyncExternalStore(store.subscribe, () => {
    const next = selector(store.getSnapshot());
    if (prevRef.current !== undefined && shallowEqual(prevRef.current, next)) {
      return prevRef.current;
    }
    prevRef.current = next;
    return next;
  });
}
